// Sichtbarkeit-Tab (design_handoff_team §7, Screens 10/13): pro Mitarbeiter (Rolle „team“) steuern, ob er ALLE
// Tickets/Rechnungen sieht oder nur die eigenen. Durchgesetzt per RLS (Migration mitarbeiter_sichtbarkeit); Admins
// sehen immer alles. Fachlogik unverändert aus SichtbarkeitTab: je Zeile nur ein Speichervorgang gleichzeitig.
import { useEffect, useState } from 'react';
import { CircleNotch, Eye, EyeSlash, IdentificationCard, Receipt, ShieldCheck, Ticket } from '@phosphor-icons/react';
import { supabase } from '../../../../lib/supabase/client';
import { ohneArchivierte } from '../../../../lib/team/archiv';
import { STUFEN, STUFEN_LABEL, STUFEN_WERTE, stufeAus, type RechnungsStufe } from '../../../../lib/team/sichtbarkeit';
import { LadeKarte, MitarbeiterAvatar } from './Bausteine';

interface Zeile {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  sieht_alle_tickets: boolean;
  sieht_alle_rechnungen: boolean;
  rechnungen_bereich: string | null;
  /** #548: volle Rechte in Zeiterfassung, Abwesenheiten, Personalakte, Monatsabschluss */
  darf_personal_verwalten: boolean;
}

const SPALTEN = 'minmax(200px,1fr) 170px 330px 170px';

function KleinTitel({ icon: I, children }: { icon: typeof Ticket; children: string }) {
  return (
    <span className="flex items-center" style={{ gap: 6, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
      <I size={12} weight="bold" />{children}
    </span>
  );
}

export function SichtbarkeitBereich({ breit, melde }: { breit: boolean; melde: (text: string, fehler?: boolean) => void }) {
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState<string | null>(null);

  useEffect(() => {
    void supabase
      .from('profiles')
      .select('id, full_name, email, avatar_url, sieht_alle_tickets, sieht_alle_rechnungen, rechnungen_bereich, darf_personal_verwalten, status')
      .eq('role', 'team')
      .order('full_name')
      .then(({ data }) => {
        setZeilen(ohneArchivierte((data || []) as (Zeile & { status: string | null })[]));
        setLaedt(false);
      });
  }, []);

  const ticketsUmschalten = async (id: string, wert: boolean) => {
    setSpeichert(`${id}-sieht_alle_tickets`);
    const { error } = await supabase.from('profiles').update({ sieht_alle_tickets: wert }).eq('id', id);
    if (error) melde(`Speichern fehlgeschlagen: ${error.message}`, true);
    else setZeilen((alt) => alt.map((z) => (z.id === id ? { ...z, sieht_alle_tickets: wert } : z)));
    setSpeichert(null);
  };

  // #548: Personal verwalten — volle Rechte im Personalbereich für alle Mitarbeiter (auch Stundenlöhne).
  const personalUmschalten = async (id: string, wert: boolean) => {
    setSpeichert(`${id}-darf_personal_verwalten`);
    const { error } = await supabase.from('profiles').update({ darf_personal_verwalten: wert }).eq('id', id);
    if (error) melde(`Speichern fehlgeschlagen: ${error.message}`, true);
    else setZeilen((alt) => alt.map((z) => (z.id === id ? { ...z, darf_personal_verwalten: wert } : z)));
    setSpeichert(null);
  };

  const setzeRechnungsStufe = async (id: string, stufe: RechnungsStufe) => {
    setSpeichert(`${id}-${stufe}`);
    const werte = STUFEN_WERTE[stufe];
    const { error } = await supabase.from('profiles').update(werte).eq('id', id);
    if (error) melde(`Speichern fehlgeschlagen: ${error.message}`, true);
    else setZeilen((alt) => alt.map((z) => (z.id === id ? { ...z, ...werte } : z)));
    setSpeichert(null);
  };

  if (laedt) return <LadeKarte />;

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <div className="immo-glas flex items-start" style={{ borderRadius: 22, padding: '16px 18px', gap: 14 }}>
        <span className="flex shrink-0 items-center justify-center" style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--info-soft)' }}>
          <ShieldCheck size={20} weight="fill" color="var(--info)" />
        </span>
        <p className="m-0" style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink2)' }}>
          Tickets: „Nur eigene" = dem Mitarbeiter, seinem Team oder einem Teamkollegen zugewiesen — plus selbst erstellte; von der Datenbank erzwungen.
          Rechnungen: „Nur eigene" erzwingt die Datenbank (zugewiesen, Teamkollege, Prüf-/Freigabe-Auftrag).
          „WEG"/„MV" blendet in App-Listen und Dashboard die jeweils andere Objektart aus — Rechnungen ohne
          Objekt und eigene bleiben sichtbar. Personal „Verwalten" gibt volle Rechte in Zeiterfassung, Abwesenheiten,
          Personalakte und Monatsabschluss für alle Mitarbeiter – inklusive Stundenlöhnen und Auszahlungen.
          Admins sehen immer alles.
        </p>
      </div>

      {zeilen.length === 0 ? (
        <div className="immo-glas text-center" style={{ borderRadius: 22, padding: 30, fontSize: 14, color: 'var(--ink3)' }}>
          Keine Mitarbeiter mit Rolle „Team" vorhanden.
        </div>
      ) : (
        <div role="table" aria-label="Sichtbarkeit je Mitarbeiter" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 6 }}>
          {breit && (
            <div role="row" className="grid" style={{ gridTemplateColumns: SPALTEN, gap: 14, padding: '10px 16px', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
              <span role="columnheader">Mitarbeiter</span>
              <span role="columnheader" className="flex items-center" style={{ gap: 6 }}><Ticket size={13} weight="bold" />Tickets</span>
              <span role="columnheader" className="flex items-center" style={{ gap: 6 }}><Receipt size={13} weight="bold" />Rechnungen</span>
              <span role="columnheader" className="flex items-center" style={{ gap: 6 }}><IdentificationCard size={13} weight="bold" />Personal</span>
            </div>
          )}
          {zeilen.map((z, i) => {
            // Zeilenweite Sperre – kein paralleles Speichern je Mitarbeiter.
            const zeileSpeichert = speichert?.startsWith(`${z.id}-`) ?? false;
            const alle = z.sieht_alle_tickets;
            const ticketLaedt = speichert === `${z.id}-sieht_alle_tickets`;
            const aktiv = stufeAus(z);
            const TIcon = ticketLaedt ? CircleNotch : alle ? Eye : EyeSlash;
            return (
              <div key={z.id} role="row" aria-busy={zeileSpeichert || undefined} className="grid items-center"
                style={{ gridTemplateColumns: breit ? SPALTEN : '1fr', gap: breit ? 14 : 10, padding: '12px 16px', borderRadius: 20, borderTop: i ? '1px solid var(--line)' : 'none' }}>
                <div role="cell" className="flex min-w-0 items-center" style={{ gap: 12 }}>
                  <MitarbeiterAvatar id={z.id} name={z.full_name || '?'} url={z.avatar_url} groesse={40} />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate" style={{ fontSize: 14.5, fontWeight: 800 }}>{z.full_name}</span>
                    <span className="truncate" style={{ fontSize: 12.5, color: 'var(--ink3)' }}>{z.email}</span>
                  </div>
                </div>

                <div role="cell" className="flex flex-col" style={{ gap: 6 }}>
                  {!breit && <KleinTitel icon={Ticket}>Tickets</KleinTitel>}
                  <button type="button" role="switch" aria-checked={alle} disabled={zeileSpeichert}
                    onClick={() => void ticketsUmschalten(z.id, !alle)}
                    data-tip={alle ? 'Sieht aktuell ALLES – klicken für „nur Eigene"' : 'Sieht nur Eigene – klicken für „Alle"'}
                    className="inline-flex items-center self-start"
                    style={{ height: 38, padding: '0 6px 0 14px', gap: 10, border: 'none', borderRadius: 19, fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
                      cursor: zeileSpeichert ? 'wait' : 'pointer', opacity: zeileSpeichert ? 0.6 : 1,
                      background: alle ? 'var(--ok-soft)' : 'var(--fill2)', color: alle ? 'var(--ok)' : 'var(--ink2)' }}>
                    <TIcon size={15} weight="bold" className={ticketLaedt ? 'animate-spin' : undefined} />
                    {alle ? 'Alle' : 'Nur eigene'}
                    <span aria-hidden className="flex" style={{ width: 44, height: 28, borderRadius: 14, padding: 2, justifyContent: alle ? 'flex-end' : 'flex-start',
                      background: alle ? 'var(--ok)' : 'var(--fill3)', transition: 'background .25s' }}>
                      <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.25)' }} />
                    </span>
                  </button>
                </div>

                <div role="cell" className="flex min-w-0 flex-col" style={{ gap: 6 }}>
                  {!breit && <KleinTitel icon={Receipt}>Rechnungen</KleinTitel>}
                  <div role="radiogroup" aria-label={`Rechnungen – ${z.full_name}`} className="flex" style={{ padding: 3, borderRadius: 15, background: 'var(--fill2)', opacity: zeileSpeichert ? 0.6 : 1 }}>
                    {STUFEN.map((stufe) => {
                      const an = aktiv === stufe;
                      const laedtStufe = speichert === `${z.id}-${stufe}`;
                      return (
                        <button key={stufe} type="button" role="radio" aria-checked={an} disabled={zeileSpeichert || an}
                          onClick={() => void setzeRechnungsStufe(z.id, stufe)}
                          data-tip={`Rechnungssichtbarkeit auf „${STUFEN_LABEL[stufe]}" setzen`}
                          className="flex min-w-0 flex-1 items-center justify-center whitespace-nowrap"
                          style={{ height: 34, padding: '0 10px', gap: 5, border: 'none', borderRadius: 12, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800,
                            cursor: zeileSpeichert ? 'wait' : an ? 'default' : 'pointer', transition: 'background .2s, color .2s',
                            background: an ? 'var(--sheet)' : 'transparent', color: an ? 'var(--ink)' : 'var(--ink2)', boxShadow: an ? '0 2px 8px -2px var(--shade)' : 'none' }}>
                          {laedtStufe && <CircleNotch size={12} weight="bold" className="animate-spin" />}
                          {STUFEN_LABEL[stufe]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div role="cell" className="flex flex-col" style={{ gap: 6 }}>
                  {!breit && <KleinTitel icon={IdentificationCard}>Personal</KleinTitel>}
                  {(() => {
                    const an = z.darf_personal_verwalten;
                    const laedtP = speichert === `${z.id}-darf_personal_verwalten`;
                    return (
                      <button type="button" role="switch" aria-checked={an} disabled={zeileSpeichert}
                        onClick={() => void personalUmschalten(z.id, !an)}
                        data-tip={an ? 'Verwaltet Personal (Zeiten, Akten, Abschluss aller) – klicken zum Entziehen' : 'Nur eigene Zeiten/Akte – klicken für volle Personal-Rechte'}
                        className="inline-flex items-center self-start"
                        style={{ height: 38, padding: '0 6px 0 14px', gap: 10, border: 'none', borderRadius: 19, fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
                          cursor: zeileSpeichert ? 'wait' : 'pointer', opacity: zeileSpeichert ? 0.6 : 1,
                          background: an ? 'var(--ok-soft)' : 'var(--fill2)', color: an ? 'var(--ok)' : 'var(--ink2)' }}>
                        {laedtP ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <IdentificationCard size={15} weight="bold" />}
                        {an ? 'Verwalten' : 'Nur eigene'}
                        <span aria-hidden className="flex" style={{ width: 44, height: 28, borderRadius: 14, padding: 2, justifyContent: an ? 'flex-end' : 'flex-start',
                          background: an ? 'var(--ok)' : 'var(--fill3)', transition: 'background .25s' }}>
                          <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.25)' }} />
                        </span>
                      </button>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
